import IEdgeData from "../../database/interfaces/IEdgeData";
import Edges from "../../database/Edges";
import { ALL_EDGE_KEYS, ZERO_GUID } from "./edgeKeys";
import { isEdgeAvailable } from "./edgeAvailability";

export { ZERO_GUID };

/** Az összes él-mező (oldalak + láthatóság) ID-it adja vissza a script objektumból. */
export const getEdgeIds = (script: any): string[] =>
    ALL_EDGE_KEYS.map(key => script[key]);

/** Az első nem-nulla él vastagságát adja vissza. */
export const getPreferredThickness = (edgeData: Edges | null, script: any): number | null => {
    const firstEdgeId = getEdgeIds(script).find(e => e && e !== ZERO_GUID);
    if (!firstEdgeId) return null;
    return edgeData?.findByID(firstEdgeId)?.Thickness ?? null;
};

/**
 * Kiválasztja a legjobb élet a megadott CompatibleEdgings listából.
 *
 * 1) CompatibleEdgings-ből méretszűrve:
 *    - ha van preferredThickness, azt matcheli először
 *    - ha nincs, a legvastagabbat választja
 * 2) Fallback: bármely él ami méretben passzol.
 */
export const pickBestEdge = (
    edgeData: Edges | null,
    compatibleEdgings: string[],
    requiredWidth: number,
    maxWidth: number | null,
    preferredThickness?: number | null
): string | null => {
    // Csak elérhető (Status = 1) élet választunk automatikusan — összhangban az EdgeContent
    // lista-szűrésével, hogy a beállított él biztosan látszódjon a selectorban.
    const sizeFilter = (e: IEdgeData) =>
        isEdgeAvailable(e) && e.Width >= requiredWidth && (maxWidth === null || e.Width <= maxWidth);

    // 1) CompatibleEdgings-ből (azonos dekór), méretszűrve, legvastagabb
    const compatibleEdgeList = compatibleEdgings
        .map(id => edgeData?.findByID(id))
        .filter((e): e is IEdgeData => !!e)
        .filter(sizeFilter);

    if (compatibleEdgeList.length > 0) {
        if (preferredThickness != null) {
            const exactMatch = compatibleEdgeList.find(e => e.Thickness === preferredThickness);
            if (exactMatch) return exactMatch.ID;
        }
        compatibleEdgeList.sort((a, b) => b.Thickness - a.Thickness);
        return compatibleEdgeList[0].ID;
    }

    // 2) Fallback: bármely él ami méretben passzol, az első
    const allEdges = edgeData?.children().filter(sizeFilter);
    if (allEdges && allEdges.length > 0) {
        if (preferredThickness != null) {
            const exactMatch = allEdges.find(e => e.Thickness === preferredThickness);
            if (exactMatch) return exactMatch.ID;
        }
        return allEdges[0].ID;
    }

    console.warn("No edge found for requiredWidth:", requiredWidth, "maxWidth:", maxWidth);
    return null;
};

/**
 * Megmondja, hogy az adott oldal élét le kell-e cserélni.
 * - Ha van kompatibilis él → csak azokat cseréljük
 * - Ha nincs kompatibilis: 1 él → cserél, több → békén hagy
 */
const shouldReplaceEdge = (
    edgeId: string,
    oldCompatSet: Set<string>,
    anyEdgeIsCompat: boolean,
    uniqueEdgeCount: number
): boolean => {
    if (!edgeId || edgeId === ZERO_GUID) return false;
    if (!anyEdgeIsCompat) return uniqueEdgeCount <= 1;
    return oldCompatSet.has(edgeId);
};

/**
 * A shouldReplaceEdge logikával lecseréli az autoEdgeId-ra azokat az oldalakat,
 * amelyek a régi board CompatibleEdgings-ében voltak.
 */
export const applyAutoEdgeWithCompatCheck = (
    script: any,
    autoEdgeId: string,
    oldCompatEdgings: string[]
): void => {
    const oldCompatSet = new Set(oldCompatEdgings);
    const currentEdges = getEdgeIds(script);
    const anyEdgeIsCompat = currentEdges.some(e => e && e !== ZERO_GUID && oldCompatSet.has(e));
    const uniqueEdgeCount = new Set(currentEdges.filter(e => e && e !== ZERO_GUID)).size;

    ALL_EDGE_KEYS.forEach(key => {
        if (shouldReplaceEdge(script[key], oldCompatSet, anyEdgeIsCompat, uniqueEdgeCount)) {
            script[key] = autoEdgeId;
        }
    });
};

/**
 * Egyedi élek összegyűjtése a script-ből: Map<edgeId, sideKeys[]>.
 */
export const collectUniqueEdges = (script: any): Map<string, string[]> => {
    const uniqueEdges = new Map<string, string[]>();
    ALL_EDGE_KEYS.forEach(key => {
        const edgeId = script[key];
        if (edgeId && edgeId !== ZERO_GUID) {
            if (!uniqueEdges.has(edgeId)) uniqueEdges.set(edgeId, []);
            uniqueEdges.get(edgeId)!.push(key);
        }
    });
    return uniqueEdges;
};

/**
 * A jelöltek közül az AZONOS VASTAGSÁGÚ él, ha van ilyen; egyébként a lista ELSŐ eleme.
 *
 * Ez a szabály két helyen azonos: dekór-váltáskor (új dekór azonos vastagságú éle) és szélesség-
 * váltáskor (azonos dekór, új szélesség). Csak a jelölt-halmaz más, a választás módja ugyanaz,
 * ezért egy helyen áll. A jelöltek SORRENDJE számít: a hívó már szűrt és rendezett listát ad
 * (a board CompatibleEdgings-e struktúra-tudatosan rendezett), így a `[0]` a legjobb tartalék.
 */
const preferSameThickness = (candidates: IEdgeData[], oldEdge: IEdgeData): IEdgeData | undefined =>
    candidates.find(e => e.Thickness === oldEdge.Thickness) ?? candidates[0];

/**
 * Minden élet lecserél ugyanolyan dekórú (ProductImage), megfelelő szélességű élre.
 * Azonos vastagságot preferál, ha nincs, az elsőt veszi abból a dekórból.
 * Fallback: bármely él megfelelő szélességgel.
 */
export const replaceEdgesBySameDecor = (
    edgeData: Edges | null,
    script: any,
    requiredWidth: number,
    maxWidth: number | null
): void => {
    const uniqueEdges = collectUniqueEdges(script);
    const widthFilter = (e: IEdgeData) =>
        isEdgeAvailable(e) && e.Width >= requiredWidth && (maxWidth === null || e.Width <= maxWidth);

    for (const [oldEdgeId, sides] of uniqueEdges) {
        const oldEdge = edgeData?.findByID(oldEdgeId);
        if (!oldEdge) continue;

        // Ugyanolyan dekórú élek közül a megfelelő szélességűt
        const sameDecorEdges = edgeData?.children()
            .filter(e => e.ProductImage === oldEdge.ProductImage && widthFilter(e)) || [];

        const replacement = sameDecorEdges.length > 0
            ? preferSameThickness(sameDecorEdges, oldEdge)
            : edgeData?.children().find(widthFilter);

        if (replacement) {
            sides.forEach(key => { script[key] = replacement.ID; });
        }
    }
};

/**
 * Board-váltáskor a RÉGI board dekorjához tartozó (oldCompatEdgings-ben lévő) éleket az ÚJ board
 * ALAP-ÉLÉRE cseréli: az új board CompatibleEdgings-ének első, méretben érvényes eleme. A lista
 * struktúra-tudatosan rendezett (a board saját struktúrája elöl, azon belül vastagság szerint csökkenő),
 * így ez a board saját struktúrájának legvastagabb érvényes éle — a választó legfelső eleme. Az idegen
 * (kézzel beállított, más dekorú) éleket békén hagyja; egyetlen él-selectornál a kézit is igazítja.
 */
export const replaceCompatEdgesToNewDecor = (
    edgeData: Edges | null,
    script: any,
    oldCompatEdgings: string[],
    newCompatEdgings: string[],
    targetDecorEdgeId: string,
    requiredWidth: number,
    maxWidth: number | null
): void => {
    const targetEdge = edgeData?.findByID(targetDecorEdgeId);
    if (!targetEdge) return;
    const targetDecor = targetEdge.ProductImage;

    const oldCompatSet = new Set(oldCompatEdgings);
    const widthFilter = (e: IEdgeData) =>
        isEdgeAvailable(e) && e.Width >= requiredWidth && (maxWidth === null || e.Width <= maxWidth);

    // Az ÚJ board dekórjának méretben érvényes élei, a CompatibleEdgings SORRENDJÉBEN (a lista
    // struktúra-tudatosan rendezett: a board saját struktúrája elöl, azon belül vastagság szerint
    // csökkenő). Ha ebből egy sincs, bármely méretben érvényes él jöhet — így a csere sosem marad el.
    const targetDecorEdges = newCompatEdgings
        .map(id => edgeData?.findByID(id))
        .filter((e): e is IEdgeData => !!e && e.ProductImage === targetDecor && widthFilter(e));
    const candidates = targetDecorEdges.length > 0
        ? targetDecorEdges
        : (edgeData?.children().filter(widthFilter) ?? []);
    if (candidates.length === 0) return;

    // Egyetlen él-selectornál a kézit is az új boardhoz igazítjuk; több él esetén csak a RÉGI board
    // dekorjához tartozókat cseréljük, az idegen (kézzel beállított) éleket békén hagyjuk.
    //
    // MINDEGYIK BEJEGYZÉS A SAJÁT VASTAGSÁGÁT TARTJA MEG: a 0.8 mm-es él az új dekór 0.8 mm-esére
    // vált, a 2 mm-es a 2 mm-esére. Korábban egyetlen, közös csere-élre esett mindegyik (a lista
    // első eleme), ezért egy 0.8 mm-es élből 2 mm-es lett. Ha az új dekórban nincs azonos vastagság,
    // a dekór-egyezés az erősebb (a lista első eleme jön); ha a dekór sincs meg, bármely méretben
    // érvényes él — ez a `candidates` fenti sorrendje.
    const uniqueEdges = collectUniqueEdges(script);
    const singleEdge = uniqueEdges.size === 1;
    for (const [oldEdgeId, sides] of uniqueEdges) {
        if (!oldCompatSet.has(oldEdgeId) && !singleEdge) continue;
        const oldEdge = edgeData?.findByID(oldEdgeId);
        const replacement = oldEdge ? preferSameThickness(candidates, oldEdge) : candidates[0];
        if (!replacement) continue;
        sides.forEach(key => { script[key] = replacement.ID; });
    }
};